import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useTimerStore } from '../../store/timerStore';
import { useProjectStore } from '../../store/projectStore';
import { useClientStore } from '../../store/clientStore';
import { categoriesAPI, subprojectsAPI, Category, ProjectSubproject } from '../../services/api';

interface TimerProps {
  onTimerStop: (data: {
    hours: number;
    projectId: string | null;
    categoryId: string | null;
    subprojectId: string | null;
    description: string;
  }) => void;
}

export const Timer: React.FC<TimerProps> = ({ onTimerStop }) => {
  const { t } = useTranslation();
  const {
    isRunning,
    isPaused,
    projectId,
    categoryId,
    subprojectId,
    description,
    start,
    pause,
    resume,
    stop,
    discard,
    updateDescription,
    updateProject,
    updateCategory,
    updateSubproject,
    getCurrentElapsed,
  } = useTimerStore();

  const { projects } = useProjectStore();
  const { clients } = useClientStore();

  const [categories, setCategories] = useState<Category[]>([]);
  const [subprojects, setSubprojects] = useState<ProjectSubproject[]>([]);
  const [displayTime, setDisplayTime] = useState('00:00:00');

  // Fetch categories and subprojects when project changes
  useEffect(() => {
    if (projectId) {
      categoriesAPI.list(projectId).then(setCategories).catch(() => setCategories([]));
      subprojectsAPI.list(projectId).then(setSubprojects).catch(() => setSubprojects([]));
    } else {
      setCategories([]);
      setSubprojects([]);
    }
  }, [projectId]);

  // Update display time every second
  useEffect(() => {
    const interval = setInterval(() => {
      if (isRunning) {
        const elapsed = getCurrentElapsed();
        const hours = Math.floor(elapsed / 3600);
        const minutes = Math.floor((elapsed % 3600) / 60);
        const seconds = elapsed % 60;
        setDisplayTime(
          `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        );
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, getCurrentElapsed]);

  // Reset display when timer is not running
  useEffect(() => {
    if (!isRunning) {
      setDisplayTime('00:00:00');
    }
  }, [isRunning]);

  const handleStart = () => {
    start(projectId || undefined, categoryId || undefined, subprojectId || undefined, description);
  };

  const handleStop = () => {
    const result = stop();
    onTimerStop(result);
  };

  // Group projects by client
  const groupedProjects = clients.map(client => ({
    client,
    projects: projects.filter(p => p.client_id === client.id && p.is_active),
  })).filter(group => group.projects.length > 0);

  const activeProjects = projects.filter(p => p.is_active);
  const activeCategories = categories.filter(c => c.is_active);
  const activeSubprojects = subprojects.filter(s => s.is_active);

  const getSelectedCategory = (): Category | undefined => {
    return categories.find(c => c.id === categoryId);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2 mb-3">
        <ClockIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Timer</h3>
        {isRunning && (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            isPaused
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 animate-pulse'
          }`}>
            {isPaused ? 'Paused' : 'Running'}
          </span>
        )}
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {/* Timer Display */}
        <div className="flex items-center gap-4">
          <div className="text-3xl font-mono font-bold text-gray-900 dark:text-white min-w-[120px]">
            {displayTime}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {!isRunning ? (
              <button
                onClick={handleStart}
                disabled={activeProjects.length === 0}
                className="inline-flex items-center justify-center w-10 h-10 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-full transition-colors"
                title="Start"
              >
                <PlayIcon className="h-5 w-5" />
              </button>
            ) : (
              <>
                {isPaused ? (
                  <button
                    onClick={resume}
                    className="inline-flex items-center justify-center w-10 h-10 bg-green-600 hover:bg-green-700 text-white rounded-full transition-colors"
                    title="Resume"
                  >
                    <PlayIcon className="h-5 w-5" />
                  </button>
                ) : (
                  <button
                    onClick={pause}
                    className="inline-flex items-center justify-center w-10 h-10 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full transition-colors"
                    title="Pause"
                  >
                    <PauseIcon className="h-5 w-5" />
                  </button>
                )}
                <button
                  onClick={handleStop}
                  disabled={!projectId}
                  className="inline-flex items-center justify-center w-10 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-full transition-colors"
                  title="Stop and Create Entry"
                >
                  <StopIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={discard}
                  className="inline-flex items-center justify-center w-10 h-10 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-full transition-colors"
                  title="Discard"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Project, Category, Description */}
        <div className="flex-1 min-w-0 flex flex-col md:flex-row gap-2">
          {/* Project */}
          <select
            value={projectId || ''}
            onChange={(e) => updateProject(e.target.value)}
            disabled={isRunning && !isPaused}
            className="min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
          >
            <option value="">Select project...</option>
            {groupedProjects.map((group) => (
              <optgroup key={group.client.id} label={group.client.name}>
                {group.projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          {/* Category */}
          <select
            value={categoryId || ''}
            onChange={(e) => updateCategory(e.target.value)}
            disabled={isRunning && !isPaused}
            className="min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
          >
            <option value="">Select category...</option>
            {activeCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* Subproject */}
          {activeSubprojects.length > 0 && (
            <select
              value={subprojectId || ''}
              onChange={(e) => updateSubproject(e.target.value)}
              disabled={isRunning && !isPaused}
              className="min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
            >
              <option value="">Subproject...</option>
              {activeSubprojects.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </select>
          )}

          {/* Description */}
          <input
            type="text"
            value={description}
            onChange={(e) => updateDescription(e.target.value)}
            placeholder="What are you working on?"
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>

      {isRunning && !projectId && (
        <p className="mt-2 text-xs text-yellow-600 dark:text-yellow-400">
          Select a project before stopping the timer to create an entry.
        </p>
      )}
    </div>
  );
};
